// setupAxios is NOT statically imported here — doing so creates a circular
// dependency: browserlessService → setupAxios → store → rootReducer →
// browserLessImageSlice → browserLessImageThunk → browserlessService.
// The lazy require() below breaks that chain.

const BROWSERLESS_BASE_URL = 'https://production-sfo.browserless.io'
const FORWARDING_ENDPOINT = '/external_service_forwarding'
const TOKEN_PLACEHOLDER = '$TOKEN'

const DEFAULT_TIMEOUTS = {
    scrape: 300,
    content: 300,
    screenshot: 125,
}

const DEFAULT_SCREENSHOT_QUERY =
    '&blockAds&stealth&--user-data-dir=~/browserless-cache-userdata&--window-size=1200'

const DEFAULT_SCREENSHOT_BODY = {
    options: { type: 'jpeg', quality: 50, encoding: 'base64' },
    gotoOptions: { timeout: 60000, waitUntil: 'networkidle2' },
}

const DEFAULT_HEADERS = {
    'content-type': 'application/json',
    'Cache-Control': 'no-cache',
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const isRetryableError = (error) => {
    if (!error?.response) return true
    const status = error.response?.status
    return status >= 500 || status === 429
}

export class BrowserlessError extends Error {
    constructor(message, { cause, status, endpoint } = {}) {
        super(message)
        this.name = 'BrowserlessError'
        this.cause = cause
        this.status = status
        this.endpoint = endpoint
    }
}

export const safeJsonParse = (value, fallback = null) => {
    if (value == null || value === '') return fallback
    if (typeof value === 'object') return value
    try {
        return JSON.parse(value)
    } catch {
        return fallback
    }
}

const extractForwardingBody = (response) => response?.data?.data?.body ?? ''

export const parseHtmlMetadata = (html) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html || ''

    const nodes = tempDiv.querySelectorAll('title,meta[name="description"],h1')
    const data = {}

    nodes.forEach((el) => {
        const nodename = el.nodeName.toLowerCase()
        data[nodename] =
            nodename === 'title' || nodename === 'h1'
                ? (el.textContent || '').trim()
                : (el.getAttribute('content') || '').trim()
    })

    let title = data?.title || ''
    let description = ''

    if (!data.meta && !data.h1) description = data?.title || ''
    else if (!data.meta) description = data.h1 || ''
    else description = data.meta || ''

    return { title, description }
}

// Covers ddg/html/ selector variants across DOM changes without blocking on a specific one.
export const parseDuckDuckGoResults = (html) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html || ''

    const titleEl =
        tempDiv.querySelector('h2.result__title a') ||
        tempDiv.querySelector('.result__title a') ||
        tempDiv.querySelector('a.result__a') ||
        tempDiv.querySelector('.result__a')

    const snippetEl =
        tempDiv.querySelector('a.result__snippet') ||
        tempDiv.querySelector('.result__snippet') ||
        tempDiv.querySelector('[class*="snippet"]')

    return {
        title: (titleEl?.textContent || '').trim(),
        description: (snippetEl?.textContent || '').trim(),
    }
}

export const parseScrapeResults = (rawBody) => {
    const parsed = safeJsonParse(rawBody, rawBody)
    const blocks = Array.isArray(parsed?.data) ? parsed.data : []

    const firstTextFromBlock = (idx) => {
        const block = blocks[idx]
        if (!block || !Array.isArray(block.results)) return ''
        const hit = block.results.find((r) => r?.text && r.text.trim().length)
        return (hit?.text || '').trim()
    }

    let title = firstTextFromBlock(0) || ''
    let description = firstTextFromBlock(1) || ''

    if (!title) {
        const allTexts = blocks
            .flatMap((b) => (b.results || []).map((r) => (r.text || '').trim()))
            .filter(Boolean)
        title = allTexts[0] || ''
    }

    if (!description) {
        const afterFirstTexts = blocks
            .slice(1)
            .flatMap((b) => (b.results || []).map((r) => (r.text || '').trim()))
            .filter(Boolean)
        description = afterFirstTexts[0] || ''
    }

    return { title, description }
}

class BrowserlessService {
    constructor({
        httpClient = null,
        forwardingEndpoint = FORWARDING_ENDPOINT,
        baseUrl = BROWSERLESS_BASE_URL,
        maxRetries = 2,
        retryDelayMs = 1000,
        timeouts = DEFAULT_TIMEOUTS,
    } = {}) {
        this._httpClientOverride = httpClient
        this.forwardingEndpoint = forwardingEndpoint
        this.baseUrl = baseUrl
        this.maxRetries = maxRetries
        this.retryDelayMs = retryDelayMs
        this.timeouts = { ...DEFAULT_TIMEOUTS, ...timeouts }
    }

    _getHttpClient() {
        // eslint-disable-next-line global-require
        return this._httpClientOverride ?? require('../../lib/redux/setupAxios').default
    }

    buildEndpoint(path, query = '') {
        const normalizedQuery =
            query.startsWith('&') || query === '' ? query : `&${query}`
        // Token is injected server-side when proxying via /external_service_forwarding.
        return `${this.baseUrl}/${path}?token=${TOKEN_PLACEHOLDER}${normalizedQuery}&stealth`
    }

    normalizeSelectors(selectors) {
        if (!Array.isArray(selectors) || selectors.length === 0) {
            throw new BrowserlessError('At least one CSS selector is required for scrape')
        }

        return selectors.map((selector) =>
            typeof selector === 'string' ? { selector } : selector
        )
    }

    async request({
        endpoint,
        body,
        timeout,
        headers = DEFAULT_HEADERS,
    }) {
        let lastError

        for (let attempt = 0; attempt <= this.maxRetries; attempt += 1) {
            try {
                const response = await this._getHttpClient().post(this.forwardingEndpoint, {
                    endpoint,
                    request_type: 'POST',
                    headers: JSON.stringify(headers),
                    body: JSON.stringify(body),
                    timeout,
                })

                return response
            } catch (error) {
                lastError = error
                const canRetry =
                    attempt < this.maxRetries && isRetryableError(error)

                if (!canRetry) break

                await sleep(this.retryDelayMs * 2 ** attempt)
            }
        }

        throw new BrowserlessError('Browserless request failed', {
            cause: lastError,
            status: lastError?.response?.status,
            endpoint,
        })
    }

    async scrape(url, selectors, options = {}) {
        if (!url?.trim()) {
            throw new BrowserlessError('URL is required for scrape')
        }

        const response = await this.request({
            endpoint: this.buildEndpoint('scrape'),
            body: {
                url,
                elements: this.normalizeSelectors(selectors),
                ...options.body,
            },
            timeout: options.timeout ?? this.timeouts.scrape,
            headers: options.headers ?? DEFAULT_HEADERS,
        })

        const rawBody = extractForwardingBody(response)
        return safeJsonParse(rawBody, rawBody)
    }

    async getContent(url, options = {}) {
        if (!url?.trim()) {
            throw new BrowserlessError('URL is required for content')
        }

        const response = await this.request({
            endpoint: this.buildEndpoint('content', options.queryParams ?? ''),
            body: { url, ...options.body },
            timeout: options.timeout ?? this.timeouts.content,
            headers: options.headers ?? DEFAULT_HEADERS,
        })

        return extractForwardingBody(response)
    }

    async takeScreenshot(url, options = {}) {
        if (!url?.trim()) {
            throw new BrowserlessError('URL is required for screenshot')
        }

        const response = await this.request({
            endpoint: this.buildEndpoint(
                'screenshot',
                options.queryParams ?? DEFAULT_SCREENSHOT_QUERY
            ),
            body: {
                url,
                options: {
                    ...DEFAULT_SCREENSHOT_BODY.options,
                    ...options.options,
                },
                gotoOptions: {
                    ...DEFAULT_SCREENSHOT_BODY.gotoOptions,
                    ...options.gotoOptions,
                },
                ...options.body,
            },
            timeout: options.timeout ?? this.timeouts.screenshot,
            headers: options.headers ?? { 'content-type': 'application/json' },
        })

        return extractForwardingBody(response)
    }
}

export const browserlessService = new BrowserlessService()

export default BrowserlessService
